import { baseUrl } from "util/APIBaseUrl";
import getRequest from "util/APIHelperGet";
import postRequest from "util/APIHelperPost";

export const resendOnboardingEmail = (customerId) => {
  return getRequest(`${baseUrl}/customer/${customerId}/resend-onboarding-email`);
};

export const copyOnboardingLink = (customerId) => {
  return getRequest(`${baseUrl}/customer/${customerId}/copy-onboarding-link`);
};

export const editCreditForCustomer = async (customerId, payload) => {
  const postUrl = `${baseUrl}/customer/${customerId}/addCredit`;

  const response = await postRequest(postUrl, JSON.stringify(payload), true, "POST");

  if (response.status !== 200) {
    const apiError = response?.payload?.error?.[0]?.description;
    const error = new Error(apiError);
    error.response = response;
    throw error;
  }

  return response;
};

export const sendCustomerInvoiceEmail = async (paymentType, id, customerId) => {
  const postUrl = `${baseUrl}/v2/customer/send-invoice-email`;
  const body = { type: paymentType, id, customerId };

  const response = await postRequest(postUrl, JSON.stringify(body), true, "POST");
  if (response.status !== 200) {
    const apiError = response?.payload?.error?.[0]?.description;
    const error = new Error(apiError);
    error.response = response;
    throw error;
  }

  return response;
};

export const getPayrollDocumentSignedUrl = async (customerId, payrollId, s3Key, contentType, size) => {
  try {
    const token = localStorage.getItem("accessToken");
    const url = `${baseUrl}/customer/${customerId}/payroll/${payrollId}/documents/signed-url?s3Key=${s3Key}&contentType=${encodeURIComponent(contentType)}&size=${size}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get signed URL: ${response.statusText}`);
    }

    const signedUrl = await response.text();

    if (!signedUrl || typeof signedUrl !== "string") {
      throw new Error("Invalid signed URL received from server");
    }

    return signedUrl;
  } catch (error) {
    throw new Error(`Failed to get signed URL: ${error.message}`);
  }
};

export const uploadPayrollDocumentToS3 = async (signedUrl, file) => {
  try {
    if (!signedUrl || typeof signedUrl !== "string") {
      throw new Error("Invalid signed URL provided");
    }

    if (!signedUrl.startsWith("http://") && !signedUrl.startsWith("https://")) {
      throw new Error(`Signed URL must be absolute. Received: ${signedUrl}`);
    }

    const response = await fetch(signedUrl, {
      method: "PUT",
      body: file,
    });

    if (!response.ok) {
      throw new Error(`Failed to upload document: ${response.statusText}`);
    }

    return response;
  } catch (error) {
    throw new Error(`Upload failed: ${error.message}`);
  }
};

export const updatePaymentStatus = async (customerId, payrollId, payload) => {
  const putUrl = `${baseUrl}/customer/${customerId}/payroll/${payrollId}/update-payment-status`;

  const response = await postRequest(putUrl, JSON.stringify(payload), true, "PUT");

  if (response.status !== 200) {
    const apiError = response?.payload?.error?.[0]?.description;
    const error = new Error(apiError);
    error.response = response;
    throw error;
  }

  return response;
};

export const downloadPayrollDocument = async (customerId, payrollId, s3Keys) => {
  try {
    const token = localStorage.getItem("accessToken");
    // When s3Keys are provided, download only those documents (per-stage);
    // otherwise download all documents for the payroll.
    const query =
      Array.isArray(s3Keys) && s3Keys.length
        ? `?s3Keys=${encodeURIComponent(s3Keys.join(","))}`
        : "";
    const url = `${baseUrl}/customer/${customerId}/payroll/${payrollId}/documents/download${query}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/zip",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to download document: ${response.statusText}`);
    }

    const contentDisposition = response.headers.get("content-disposition");
    let filename = "";
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, "");
      }
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.style.display = "none";
    link.href = blobUrl;
    if (filename) {
      link.download = filename;
    }

    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    }, 100);

    return { success: true };
  } catch (error) {
    throw new Error(`Download failed: ${error.message}`);
  }
};
